import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { get, post } from "src/utils/networkUtils";
import MidniteOddsSection from "src/sections/@dashboard/opta/MidniteOddsSection";

function TournamentMissingAlert({ message }) {
  const navigate = useNavigate();
  return (
    <Alert
      severity="warning"
      sx={{ mt: 2 }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => navigate("/dashboard/ecff/opta-import/tournaments")}
        >
          Go to Tournament Import
        </Button>
      }
    >
      {message}
    </Alert>
  );
}

const STATUS_COLORS = {
  running: "info",
  success: "success",
  partial: "warning",
  failed: "error",
};

const POLL_INTERVAL_MS = 3000;

// The sync log is shared with the Midnite feed. `midnite_odds` is excluded on
// purpose: it ticks every minute and logs a row on every failure, so it would
// fill the latest 20 within 20 minutes. The Odds section shows its own state.
const SYNC_ENTITY_TYPES = "squads,matches,midnite_pairing";

function summariseCounts(counts) {
  if (!counts || typeof counts !== "object") return "-";
  return Object.entries(counts)
    .map(([k, v]) => {
      if (v && typeof v === "object" && "inserted" in v) {
        const skipped = "skipped" in v ? `, ${v.skipped} skipped` : "";
        return `${k}: ${v.inserted} new / ${v.updated ?? 0} upd${skipped}`;
      }
      return `${k}: ${JSON.stringify(v)}`;
    })
    .join(" · ");
}

function findRun(syncs, runId) {
  if (!runId) return null;
  return syncs.find((s) => s.id === runId) ?? null;
}

function isInFlight(run, runId) {
  if (!runId) return false;
  // Either the row hasn't shown up yet, or it's still flagged 'running'.
  return !run || run.status === "running";
}

function RunStatusAlert({ runId, run, kickoffError, kindLabel }) {
  if (kickoffError) {
    if (
      typeof kickoffError === "string" &&
      kickoffError.includes("not in the local database")
    ) {
      return <TournamentMissingAlert message={kickoffError} />;
    }
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {kickoffError}
      </Alert>
    );
  }
  if (!runId) return null;
  if (isInFlight(run, runId)) {
    return (
      <Alert
        severity="info"
        icon={<CircularProgress size={16} />}
        sx={{ mt: 2 }}
      >
        {kindLabel} import running… (run id: {String(runId).slice(0, 8)}…)
      </Alert>
    );
  }
  if (run.status === "success" || run.status === "partial") {
    return (
      <Alert
        severity={run.status === "partial" ? "warning" : "success"}
        sx={{ mt: 2 }}
      >
        Done - {summariseCounts(run.counts)}
      </Alert>
    );
  }
  // failed
  return (
    <Alert severity="error" sx={{ mt: 2 }}>
      Failed - {run.error ?? "unknown error"}
    </Alert>
  );
}

export default function OptaImportPage() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTcId, setSelectedTcId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [kickoffBusy, setKickoffBusy] = useState(null); // 'squads' | 'matches' | null
  const [squadsRunId, setSquadsRunId] = useState(null);
  const [matchesRunId, setMatchesRunId] = useState(null);
  const [squadsError, setSquadsError] = useState(null);
  const [matchesError, setMatchesError] = useState(null);
  const [syncs, setSyncs] = useState([]);

  const refreshSyncs = useCallback(() => {
    get(`/admin/opta/syncs?limit=20&entityType=${SYNC_ENTITY_TYPES}`)
      .then((r) => {
        setSyncs(r?.data ?? []);
      })
      .catch(() => setSyncs([]));
  }, []);

  useEffect(() => {
    get('/admin/opta/tournaments?filter=allowed')
      .then(r => {
        const items = r?.data ?? [];
        setTournaments(items);
        if (items.length > 0) setSelectedTcId(items[0].id);
      })
      .catch(() => setTournaments([]));
    refreshSyncs();
  }, [refreshSyncs]);

  const squadsRun = findRun(syncs, squadsRunId);
  const matchesRun = findRun(syncs, matchesRunId);
  const squadsInFlight = isInFlight(squadsRun, squadsRunId);
  const matchesInFlight = isInFlight(matchesRun, matchesRunId);

  // Poll the runs table while any tracked import is still in flight.
  useEffect(() => {
    if (!squadsInFlight && !matchesInFlight) return undefined;
    const handle = setInterval(refreshSyncs, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [squadsInFlight, matchesInFlight, refreshSyncs]);

  const resolvedUuid = () => {
    const tc = tournaments.find(t => t.id === selectedTcId);
    return tc?.opta_tournament_calendar_id ?? '';
  };

  const runSquads = async () => {
    const uuid = resolvedUuid();
    if (!uuid) {
      setSquadsError("Pick or enter a tournament UUID first.");
      return;
    }
    setSquadsError(null);
    setSquadsRunId(null);
    setKickoffBusy("squads");
    try {
      const r = await post("/admin/opta/squads/import", {
        tournamentCalendarUuid: uuid,
      });
      const runId = r?.data?.runId ?? null;
      if (!runId) throw new Error("Backend returned no runId");
      setSquadsRunId(runId);
    } catch (err) {
      setSquadsError(
        err?.response?.data?.message ?? err?.message ?? "Failed to start import"
      );
    } finally {
      setKickoffBusy(null);
      refreshSyncs();
    }
  };

  const runMatches = async () => {
    const uuid = resolvedUuid();
    if (!uuid) {
      setMatchesError("Pick or enter a tournament UUID first.");
      return;
    }
    setMatchesError(null);
    setMatchesRunId(null);
    setKickoffBusy("matches");
    const body = { tournamentCalendarUuid: uuid };
    if (fromDate) body.fromDate = new Date(fromDate).toISOString();
    if (toDate) body.toDate = new Date(toDate).toISOString();
    try {
      const r = await post("/admin/opta/matches/import", body);
      const runId = r?.data?.runId ?? null;
      if (!runId) throw new Error("Backend returned no runId");
      setMatchesRunId(runId);
    } catch (err) {
      setMatchesError(
        err?.response?.data?.message ?? err?.message ?? "Failed to start import"
      );
    } finally {
      setKickoffBusy(null);
      refreshSyncs();
    }
  };

  return (
    <>
      <Helmet>
        <title>OPTA Import | EngageCraft Fantasy Football Admin</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          OPTA Import
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Server-to-server import from the OPTA GraphQL feed. Pick a tournament,
          then trigger Squads or Matches. Imports run in the background - the
          page polls every {POLL_INTERVAL_MS / 1000}s for completion.
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                select
                label="Tournament"
                value={selectedTcId}
                  onChange={(e) => setSelectedTcId(e.target.value)}
                sx={{ minWidth: 320 }}
              >
                {tournaments.length === 0 && (
                  <MenuItem value="" disabled>
                    No tournaments yet
                  </MenuItem>
                )}
                  {tournaments.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                      {t.competition_name ?? "(unnamed)"} {t.season_label ?? ""}{" "}
                      - {t.opta_tournament_calendar_id?.slice(0, 8)}…
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          sx={{ mb: 3 }}
        >
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6">Squads</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upserts contestants + persons from the <code>squads</code>{" "}
                query.
              </Typography>
              <Button
                variant="contained"
                onClick={runSquads}
                disabled={kickoffBusy !== null || squadsInFlight}
              >
                {kickoffBusy === "squads" ? (
                  <CircularProgress size={20} />
                ) : (
                  "Import Squads"
                )}
              </Button>
              <RunStatusAlert
                runId={squadsRunId}
                run={squadsRun}
                kickoffError={squadsError}
                kindLabel="Squads"
              />
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6">Matches</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upserts fixtures + replaces match_events from{" "}
                <code>matches.liveData</code>.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  type="datetime-local"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
                <TextField
                  type="datetime-local"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Stack>
              <Button
                variant="contained"
                onClick={runMatches}
                disabled={kickoffBusy !== null || matchesInFlight}
              >
                {kickoffBusy === "matches" ? (
                  <CircularProgress size={20} />
                ) : (
                  "Import Matches"
                )}
              </Button>
              <RunStatusAlert
                runId={matchesRunId}
                run={matchesRun}
                kickoffError={matchesError}
                kindLabel="Matches"
              />
            </CardContent>
          </Card>
        </Stack>

        <MidniteOddsSection onRunComplete={refreshSyncs} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          Recent runs
        </Typography>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Tournament UUID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Counts</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      No runs yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  syncs.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        {s.created_at
                          ? new Date(s.created_at).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>{s.entity_type}</TableCell>
                      <TableCell>
                        <Box sx={{ fontFamily: "monospace", fontSize: 11 }}>
                          {s.opta_tournament_calendar_uuid?.slice(0, 12) ?? "-"}
                          …
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.status}
                          size="small"
                          color={STATUS_COLORS[s.status] ?? "default"}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {summariseCounts(s.counts)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          color: "error.main",
                          maxWidth: 320,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.error ?? ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </>
  );
}
